import * as React from 'react'
import { useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { FetchStatuses } from 'builder/modules/constants'
import { useI18n } from 'builder/hooks/useI18n'
import { ResumeCritiqueStatuses } from 'builder/modules/resumeReview'
import { useConfig } from 'builder/hooks/useConfig'
import { useImageLoader } from './hooks'
import REVIEW_AVATAR from './review-avatar.png'

import {
  CritiqueFailed,
  CritiqueInReview,
  DocumentPreviewContainer,
  DocumentPreviewImage,
  DocumentPreviewPlaceholder,
  DocumentPreviewUnavailable,
  Spinner,
  Error,
  ErrorClose,
  Close,
  CritiqueFinished,
  ArrowRight,
  StarContainer,
  Star,
  ReviewAvatar,
  ReadReview,
} from './styles'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  src: string | null
  showUnavailablePreview?: boolean
  critiqueReviewStatus?: `${ResumeCritiqueStatuses}`
  viewResumeCritiqueReview?: () => void
}

export const DocumentImagePreview = ({
  src,
  showUnavailablePreview = false,
  critiqueReviewStatus,
  viewResumeCritiqueReview,
  ...rest
}: Props) => {
  const previewStatus = useImageLoader(src)
  const isReady = previewStatus === FetchStatuses.loaded && typeof src === 'string'
  const { i18n } = useI18n()
  const [showCritiqueFailed, setShowCritiqueFailed] = useState(true)
  const config = useConfig()
  const critiqueEntryPoints = config?.features.critiqueEntryPoints

  React.useEffect(() => {
    if (critiqueReviewStatus === ResumeCritiqueStatuses.finished && critiqueEntryPoints) {
      trackInternalEvent('see_resume_critique_banner', { where: 'dash' })
    }
  }, [critiqueReviewStatus])

  return (
    <DocumentPreviewContainer {...rest}>
      {critiqueReviewStatus && critiqueEntryPoints && (
        <>
          {critiqueReviewStatus === ResumeCritiqueStatuses.pending && (
            <CritiqueInReview>
              <Spinner />
              {i18n.t('builder.critique_entry_points.image_preview.review')}
            </CritiqueInReview>
          )}
          {critiqueReviewStatus === ResumeCritiqueStatuses.failed &&
            showCritiqueFailed &&
            !localStorage.getItem('hideCritiqueFailed') && (
              <CritiqueFailed onClick={e => e.preventDefault()}>
                <Error>
                  <ErrorClose />
                </Error>
                {i18n.t('builder.critique_entry_points.image_preview.failed')}
                <Close
                  onClick={() => {
                    localStorage.setItem('hideCritiqueFailed', 'true')
                    setShowCritiqueFailed(false)
                  }}
                />
              </CritiqueFailed>
            )}
          {critiqueReviewStatus === ResumeCritiqueStatuses.finished && (
            <CritiqueFinished
              onClick={e => {
                e.preventDefault()
                viewResumeCritiqueReview && viewResumeCritiqueReview()
              }}
            >
              <StarContainer>
                <Star />
              </StarContainer>
              <ReviewAvatar src={REVIEW_AVATAR} />
              <ReadReview>{i18n.t('builder.critique_entry_points.image_preview.read')}</ReadReview>
              <ArrowRight />
            </CritiqueFinished>
          )}
        </>
      )}
      {isReady ? <DocumentPreviewImage alt="Document" src={src} /> : <DocumentPreviewPlaceholder />}
      {showUnavailablePreview && (
        <DocumentPreviewUnavailable>
          {i18n.t('builder.dashboard.preview_unavailable')}
        </DocumentPreviewUnavailable>
      )}
    </DocumentPreviewContainer>
  )
}
